/**
 * MetricTile Component
 * 
 * A theme-aware metric display component that adapts its border, shadow, and background
 * based on the active theme. Designed for dashboard metrics and KPIs.
 * 
 * Features:
 * - Adaptive theming (light/dark mode)
 * - Semantic color variants (success, warning, error, info)
 * - JetBrains Mono for metric values
 * - Inter for labels and descriptions
 *
 * Decision: ADR-057 — colour carries QC status and nothing else.
 *
 * `variant` is a claim about the number, not decoration. A count of things
 * that exist — total users, active organisations, templates — is inventory
 * and takes `default`: the figure reads in the content colour and the icon
 * sits back in the secondary one. Reach for `error` / `warning` / `success`
 * only where the value itself is a verdict someone must act on, because
 * those variants tint the whole tile and spend the colours that mean pass,
 * review and fail everywhere else in the product.
 *
 * In particular an "Inactive: 0" tile is not an error, and rendering it in
 * red — which four admin pages did — tells people something is wrong when
 * nothing is.
 * 
 * @example
 * ```tsx
 * <MetricTile
 *   label="Total Jobs"
 *   value="1,234"
 *   variant="success"
 *   trend="+12.5%"
 *   icon={<CheckCircle2 />}
 * />
 * ```
 */
import React from 'react';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

export type MetricVariant = 'default' | 'success' | 'warning' | 'error' | 'info';

interface MetricTileProps {
  /** Display label (uses Inter font) */
  label: string;
  /** Metric value (uses JetBrains Mono font) */
  value: string | number;
  /** Optional subtitle/description */
  description?: string;
  /** Color variant for semantic meaning */
  variant?: MetricVariant;
  /** Optional trend indicator (e.g., "+12.5%", "-3.2%") */
  trend?: string;
  /** Optional icon (Lucide icon component) */
  icon?: React.ReactNode;
  /** Optional Lucide icon component (for TypeScript) */
  IconComponent?: LucideIcon;
  /** Additional CSS classes */
  className?: string;
  /** Click handler */
  onClick?: () => void;
}

/**
 * MetricTile - Adaptive metric display component
 */
export const MetricTile: React.FC<MetricTileProps> = ({
  label,
  value,
  description,
  variant = 'default',
  trend,
  icon,
  IconComponent,
  className,
  onClick,
}) => {
  // Determine variant styles
  const variantStyles = {
    default: 'border-border hover:border-border-strong',
    success: 'border-qc-success/30 bg-qc-success-bg hover:border-qc-success',
    warning: 'border-qc-warning/30 bg-qc-warning-bg hover:border-qc-warning',
    error: 'border-qc-error/30 bg-qc-error-bg hover:border-qc-error',
    info: 'border-qc-info/30 bg-qc-info-bg hover:border-qc-info',
  };

  const valueColorStyles = {
    default: 'text-text-primary',
    success: 'text-qc-success',
    warning: 'text-qc-warning',
    error: 'text-qc-error',
    info: 'text-qc-info',
  };

  const iconColorStyles = {
    default: 'text-text-secondary',
    success: 'text-qc-success',
    warning: 'text-qc-warning',
    error: 'text-qc-error',
    info: 'text-qc-info',
  };

  return (
    <div
      className={cn(
        // Base styles - adaptive background.
        // A 1px border, not 2: a tile is a container, and at 2px the outline
        // competes with the number it is holding (ADR-057).
        'relative overflow-hidden rounded-lg',
        'bg-bg-surface border transition-colors duration-150',
        'shadow-sm',
        // Variant-specific styles
        variantStyles[variant],
        // Interactive. No scale on hover: a grid of tiles that each grow when
        // the pointer crosses them is noise, and it nudges its neighbours.
        onClick && 'cursor-pointer',
        className
      )}
      onClick={onClick}
    >
      <div className="p-6">
        {/* Header: Icon + Label */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <p className="text-sm font-medium text-text-secondary font-ui mb-1">
              {label}
            </p>
          </div>
          {(icon || IconComponent) && (
            <div className={cn('flex-shrink-0 ml-3', iconColorStyles[variant])}>
              {IconComponent ? <IconComponent className="h-5 w-5" /> : icon}
            </div>
          )}
        </div>

        {/* Value - JetBrains Mono for data clarity */}
        <div className={cn(
          'text-3xl font-semibold font-data tracking-tight mb-2',
          valueColorStyles[variant]
        )}>
          {value}
        </div>

        {/* Description or Trend */}
        {(description || trend) && (
          <div className="flex items-center justify-between text-sm">
            {description && (
              <span className="text-text-tertiary font-ui">
                {description}
              </span>
            )}
            {trend && (
              <span
                className={cn(
                  'font-data text-xs font-medium',
                  trend.startsWith('+') || trend.startsWith('↑')
                    ? 'text-qc-success'
                    : trend.startsWith('-') || trend.startsWith('↓')
                    ? 'text-qc-error'
                    : 'text-text-secondary'
                )}
              >
                {trend}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * MetricTileGrid - Container for multiple metric tiles
 */
interface MetricTileGridProps {
  children: React.ReactNode;
  columns?: 1 | 2 | 3 | 4;
  className?: string;
}

export const MetricTileGrid: React.FC<MetricTileGridProps> = ({
  children,
  columns = 3,
  className,
}) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={cn('grid gap-6', gridCols[columns], className)}>
      {children}
    </div>
  );
};
